/**
 * Request Lifecycle Management System
 *
 * This module provides unified request lifecycle tracking and management.
 * It maintains request state throughout the entire lifecycle including retries,
 * handles memory cleanup, and provides performance monitoring integration.
 */

import {
  type RequestContext,
  type RequestConfig,
  type RequestUpdate,
  type PerformanceMetrics,
  type CoordinatorConfig,
  type CircuitBreakerState,
  type IRequestTracker,
  RequestStatus,
} from '../../../types/api';
import { DEFAULT_COORDINATOR_CONFIG } from '../../../types/api/integration.types';
import { dayjs } from '../../../utils/date';

import type {
  CoreRequestMetadata,
  RequestState,
} from '../../../types/api/request.types';

// =============================================================================
// REQUEST TRACKER IMPLEMENTATION
// =============================================================================

export class RequestTracker implements IRequestTracker {
  private state: RequestState;
  private config: CoordinatorConfig;
  private cleanupTimer?: NodeJS.Timeout;

  constructor(config: Partial<CoordinatorConfig> = {}) {
    this.config = { ...DEFAULT_COORDINATOR_CONFIG, ...config };
    this.state = {
      activeRequests: new Map(),
      completedRequests: new Map(),
      circuitBreakerStates: new Map(),
      performanceMetrics: new Map(),
      lastCleanup: dayjs().valueOf(),
    };

    this.startCleanupTimer();
  }

  /**
   * Creates a new request context and starts tracking it
   */
  createRequest(config: RequestConfig): RequestContext {
    const metadata = createRequestMetadata(config);
    const startTime = dayjs().valueOf();

    const context: RequestContext = {
      id: metadata.requestId,
      config,
      startTime,
      status: RequestStatus.PENDING,
      retryCount: metadata.retryCount || 0,
      // `maxAttempts` is the TOTAL number of attempts (the initial request plus
      // retries), whereas the retry engine caps on `maxRetries` = retries beyond
      // the initial attempt. Translate here so e.g. maxAttempts:4 yields 1
      // initial + 3 retries = 4 total dispatches.
      maxRetries: Math.max(0, this.config.retry.maxAttempts - 1),
      metadata,
      performanceMetrics: this.config.enablePerformanceMonitoring
        ? createPerformanceMetrics(metadata.requestId, startTime)
        : undefined,
    };

    this.state.activeRequests.set(context.id, context);

    if (this.config.enableLogging) {
      console.log(`📝 Request tracking started: ${context.id}`, {
        method: config.method,
        url: config.url,
        retryCount: context.retryCount,
      });
    }

    return context;
  }

  /**
   * Updates an existing request context
   */
  updateRequest(id: string, update: RequestUpdate): void {
    const context = this.state.activeRequests.get(id);
    if (!context) {
      console.warn(`⚠️ Attempted to update non-existent request: ${id}`);
      return;
    }

    // Update context properties
    if (update.status) {
      context.status = update.status;
    }

    if (update.retryCount !== undefined) {
      context.retryCount = update.retryCount;
    }

    if (update.lastError) {
      context.lastError = update.lastError;
    }

    if (update.response) {
      context.response = update.response;
      context.endTime = dayjs().valueOf();
    }

    // Update performance metrics if enabled
    if (this.config.enablePerformanceMonitoring && update.performanceMetrics) {
      context.performanceMetrics = {
        ...context.performanceMetrics,
        ...update.performanceMetrics,
      };

      this.state.performanceMetrics.set(id, context.performanceMetrics);
    }

    // Log status changes in development
    if (this.config.enableLogging && update.status) {
      console.log(`🔄 Request status updated: ${id}`, {
        status: update.status,
        retryCount: context.retryCount,
        duration: context.endTime
          ? calculateDuration(context.startTime, context.endTime)
          : undefined,
      });
    }
  }

  /**
   * Marks a request as complete and moves it to completed requests
   */
  completeRequest(id: string): void {
    const context = this.state.activeRequests.get(id);
    if (!context) {
      console.warn(`⚠️ Attempted to complete non-existent request: ${id}`);
      return;
    }

    // Set completion time if not already set
    if (!context.endTime) {
      context.endTime = dayjs().valueOf();
    }

    // Update performance metrics with final duration
    if (context.performanceMetrics) {
      context.performanceMetrics.endTime = context.endTime;
      context.performanceMetrics.duration = calculateDuration(
        context.startTime,
        context.endTime
      );
      context.performanceMetrics.totalDuration =
        context.performanceMetrics.duration;

      this.state.performanceMetrics.set(id, context.performanceMetrics);
    }

    // Move from active to completed
    this.state.activeRequests.delete(id);
    this.state.completedRequests.set(id, context);

    if (this.config.enableLogging) {
      const duration = calculateDuration(context.startTime, context.endTime);
      console.log(`✅ Request completed: ${id}`, {
        status: context.status,
        duration,
        retryCount: context.retryCount,
      });
    }
  }

  /**
   * Gets all currently active requests
   */
  getActiveRequests(): RequestContext[] {
    return Array.from(this.state.activeRequests.values());
  }

  /**
   * Gets a specific request by ID (active or completed)
   */
  getRequest(id: string): RequestContext | undefined {
    return (
      this.state.activeRequests.get(id) || this.state.completedRequests.get(id)
    );
  }

  /**
   * Gets performance metrics for a specific request
   */
  getPerformanceMetrics(id: string): PerformanceMetrics | undefined {
    return this.state.performanceMetrics.get(id);
  }

  /**
   * Gets all performance metrics
   */
  getAllPerformanceMetrics(): PerformanceMetrics[] {
    return Array.from(this.state.performanceMetrics.values());
  }

  /**
   * Cleans up old completed requests to prevent memory leaks
   */
  cleanupCompletedRequests(): void {
    const now = dayjs().valueOf();
    const maxAge = this.config.memoryCleanupInterval;
    let cleanedCount = 0;

    // Clean up old completed requests
    for (const [id, context] of this.state.completedRequests.entries()) {
      if (context.endTime && now - context.endTime > maxAge) {
        this.state.completedRequests.delete(id);
        this.state.performanceMetrics.delete(id);
        cleanedCount++;
      }
    }

    // Clean up orphaned performance metrics
    for (const [id] of this.state.performanceMetrics.entries()) {
      if (
        !this.state.activeRequests.has(id) &&
        !this.state.completedRequests.has(id)
      ) {
        this.state.performanceMetrics.delete(id);
        cleanedCount++;
      }
    }

    this.state.lastCleanup = now;

    if (this.config.enableLogging && cleanedCount > 0) {
      console.log(`🧹 Cleaned up ${cleanedCount} old request records`);
    }
  }

  /**
   * Gets current state statistics
   */
  getStateStatistics(): {
    activeRequests: number;
    completedRequests: number;
    performanceMetrics: number;
    circuitBreakerStates: number;
    lastCleanup: number;
  } {
    return {
      activeRequests: this.state.activeRequests.size,
      completedRequests: this.state.completedRequests.size,
      performanceMetrics: this.state.performanceMetrics.size,
      circuitBreakerStates: this.state.circuitBreakerStates.size,
      lastCleanup: this.state.lastCleanup,
    };
  }

  /**
   * Updates circuit breaker state for an endpoint
   */
  updateCircuitBreakerState(
    endpoint: string,
    state: CircuitBreakerState
  ): void {
    this.state.circuitBreakerStates.set(endpoint, state);
  }

  /**
   * Gets circuit breaker state for an endpoint
   */
  getCircuitBreakerState(endpoint: string): CircuitBreakerState | undefined {
    return this.state.circuitBreakerStates.get(endpoint);
  }

  /**
   * Gets all circuit breaker states
   */
  getAllCircuitBreakerStates(): Map<string, CircuitBreakerState> {
    return new Map(this.state.circuitBreakerStates);
  }

  /**
   * Starts the automatic cleanup timer
   */
  private startCleanupTimer(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }

    this.cleanupTimer = setInterval(() => {
      this.cleanupCompletedRequests();
    }, this.config.memoryCleanupInterval);
    // Don't let the cleanup timer keep a Node/test event loop alive (no-op on RN/browser).
    (this.cleanupTimer as unknown as { unref?: () => void }).unref?.();
  }

  /**
   * Stops the cleanup timer and cleans up resources
   */
  destroy(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = undefined;
    }

    // Final cleanup
    this.cleanupCompletedRequests();

    if (this.config.enableLogging) {
      const stats = this.getStateStatistics();
      console.log('🔧 Request tracker destroyed', stats);
    }
  }
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

export function generateRequestId(): string {
  return `req_${dayjs().valueOf()}_${Math.random().toString(36).substring(2, 9)}`;
}

export function calculateDuration(startTime: number, endTime?: number): number {
  return (endTime || dayjs().valueOf()) - startTime;
}

export function createRequestMetadata(
  config: RequestConfig
): CoreRequestMetadata {
  return {
    requestId: config.metadata?.requestId || generateRequestId(),
    timestamp: dayjs().valueOf(),
    skipAuth: config.metadata?.skipAuth,
    retryCount: config.metadata?.retryCount || 0,
    isRetry: config.metadata?.isRetry || false,
    parentRequestId: config.metadata?.parentRequestId,
    endpoint: config.url,
    method: config.method?.toUpperCase(),
  };
}

export function createPerformanceMetrics(
  requestId: string,
  startTime: number
): PerformanceMetrics {
  return {
    requestId,
    startTime,
    retryCount: 0,
  };
}

// =============================================================================
// FACTORY FUNCTION
// =============================================================================

/**
 * Creates a new request tracker instance
 */
export function createRequestTracker(
  config?: Partial<CoordinatorConfig>
): IRequestTracker {
  return new RequestTracker(config);
}

// =============================================================================
// SINGLETON INSTANCE
// =============================================================================

/**
 * Default request tracker instance
 * Used by the request coordinator
 */
export const defaultRequestTracker = createRequestTracker();
